/* eslint-disable prettier/prettier */
/* eslint-disable */
/* prettier-ignore-file */
import { FC, useEffect, useMemo, useState } from 'react';
import styles from '../../../styles/components/pageComponents/manageUsers/ManageUsers.module.css';
import { ReactComponent as AllUserIcon } from '../../../assets/profile/a.svg';
import { ReactComponent as LearnerIcon } from '../../../assets/profile/l.svg';
import { ReactComponent as InviteesIcon } from '../../../assets/profile/i.svg';
import view from '../../../assets/profile/View.png';
import edit from '../../../assets/profile/Edit.png';
import deletee from '../../../assets/profile/Delete.png';
import msg from '../../../assets/profile/Message.png';
import left from '../../../assets/profile/left.png';
import right from '../../../assets/profile/right.png';
import down from '../../../assets/profile/Vector (13).png';
import profileDefault from '../../../assets/profile/profile.svg';

export type UserRole = 'learner' | 'invitee' | null;

export interface User {
	id: string;
	avatarUrl?: string;
	fullName: string;
	enrolledProgram?: string;
	iccCredentials?: string;
	role: UserRole;

	// Keep program + enrollment ID together
	enrollments: {
		programName: string;
		enrollmentId: string;
	}[];

	// Keep this if other parts of your component use it
	enrollmentId: string[];
	canDelete?: boolean;
}

interface ManageUsersProps {
	users: User[];
	pageSize?: number;
	onView?: (user: User) => void;
	onEdit?: (user: User) => void;
	onDelete?: (user: User) => void;
}

type TabType = 'all' | 'learner' | 'invitee';

const ManageUsers: FC<ManageUsersProps> = ({
	users,
	pageSize = 8,
	onView,
	onEdit,
	onDelete,
}) => {
	const [activeTab, setActiveTab] = useState<TabType>('all');
	const [currentPage, setCurrentPage] = useState(1);

	const [roleSortOrder, setRoleSortOrder] = useState<'asc' | 'desc' | null>(
		null
	);

	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 768);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// 🔹 Filter logic
	const filteredUsers = useMemo(() => {
		if (activeTab === 'learner') {
			return users.filter(u => u.role === 'learner');
		}

		if (activeTab === 'invitee') {
			return users.filter(u => !u.role || u.role === 'invitee');
		}

		return users;
	}, [activeTab, users]);

	const sortedUsers = useMemo(() => {
		if (!roleSortOrder) return filteredUsers;

		return [...filteredUsers].sort((a, b) => {
			const roleA = a.role ?? '';
			const roleB = b.role ?? '';

			if (roleSortOrder === 'asc') {
				return roleA.localeCompare(roleB);
			} else {
				return roleB.localeCompare(roleA);
			}
		});
	}, [filteredUsers, roleSortOrder]);

	const handleRoleSort = () => {
		setCurrentPage(1);

		setRoleSortOrder(prev => {
			if (prev === 'asc') return 'desc';
			if (prev === 'desc') return 'asc';
			return 'asc';
		});
	};

	// 🔹 Pagination logic
	const totalPages = Math.ceil(sortedUsers.length / pageSize);

	const paginatedUsers = useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		return sortedUsers.slice(start, start + pageSize);
	}, [sortedUsers, currentPage, pageSize]);

	const renderRole = (role: UserRole) => {
		if (!role) {
			return <span className={styles.invitee}>Invitee</span>;
		}

		if (role === 'learner') {
			return <span className={styles.learner}>Learner</span>;
		}

		return <span className={styles.defaultRole}>{role}</span>;
	};

	const getPageNumbers = () => {
		const pages: (number | string)[] = [];

		if (totalPages <= 5) {
			return Array.from({ length: totalPages }, (_, i) => i + 1);
		}

		pages.push(1);

		if (currentPage > 3) {
			pages.push('left-ellipsis');
		}

		const start = Math.max(2, currentPage - 1);
		const end = Math.min(totalPages - 1, currentPage + 1);

		for (let i = start; i <= end; i++) {
			pages.push(i);
		}

		if (currentPage < totalPages - 2) {
			pages.push('right-ellipsis');
		}

		pages.push(totalPages);

		return pages;
	};
	const formatPrograms = (programs?: string) => {
		if (!programs) return 'No program enrolled';

		const maxLength = 45;

		if (programs.length <= maxLength) {
			return programs;
		}

		return `${programs.slice(0, maxLength)}...`;
	};

	const bottomUI = (
		<div className={styles.bottom}>
			{totalPages > 1 && (
				<div className={styles.pagination}>
					{totalPages > 5 && (
						<button
							disabled={currentPage === 1}
							onClick={() => setCurrentPage(p => p - 1)}
						>
							<img src={left} alt="Previous page" />
						</button>
					)}

					{getPageNumbers().map(page =>
						typeof page === 'string' ? (
							<span key={page} className={styles.ellipsis}>
								...
							</span>
						) : (
							<button
								key={page}
								className={
									currentPage === page
										? styles.activePage
										: ''
								}
								onClick={() => setCurrentPage(page)}
							>
								{page}
							</button>
						)
					)}

					{totalPages > 5 && (
						<button
							disabled={currentPage === totalPages}
							onClick={() => setCurrentPage(p => p + 1)}
						>
							<img src={right} alt="Next page" />
						</button>
					)}
				</div>
			)}

			<div className={styles.showingText}>
				Showing {paginatedUsers.length} of {filteredUsers.length}
			</div>
		</div>
	);

	return (
		<div className={styles.container}>
			{/* {isMobile && bottomUI} */}
			{isMobile && sortedUsers.length > 0 && bottomUI}
			{/* Tabs */}

			<div className={styles.tabsWrapper}>
				<div className={styles.tabs}></div>
			</div>

			{/* Table */}
			{sortedUsers.length === 0 && (
				<div className={styles.emptyStateWrapper}>
					<div className={styles.emptyState}>
						<p className={styles.emptyTitle}>No users found</p>
						<p className={styles.emptySubtitle}>
							There are no users available.
						</p>
					</div>
				</div>
			)}
			{sortedUsers.length > 0 && (
				<div className={styles.table}>
					<div className={styles.header}>
						<div>Full Name</div>
						<div>Program</div>
						<div>ICC Credentials</div>
						<div>Role</div>
						<div>Quick Actions</div>
					</div>

					{sortedUsers.length === 0 ? (
						<div className={styles.emptyState}>
							<p className={styles.emptyTitle}>
								No matching users found
							</p>
							<p className={styles.emptySubtitle}>
								Try adjusting your search or filters.
							</p>
						</div>
					) : (
						paginatedUsers.map(user =>
							isMobile ? (
								/* ✅ MOBILE CARD */
								<div key={user.id} className={styles.row}>
									<div className={styles.topRow}>
										<div className={styles.nameCell}>
											<div className={styles.avatar}>
												{user.avatarUrl ? (
													<img
														src={
															user.avatarUrl ||
															profileDefault
														}
														alt={user.fullName}
														onError={e => {
															const target =
																e.target as HTMLImageElement;
															target.onerror =
																null; // prevent infinite loop
															target.src =
																profileDefault;
														}}
													/>
												) : (
													<div
														className={
															styles.placeholderAvatar
														}
													/>
												)}
											</div>

											<span className={styles.fullName}>
												{user.fullName}
											</span>
										</div>

										<div className={styles.actions}>
											<div className={styles.tooltip}>
												<button
													className={styles.iconbtn}
													onClick={() =>
														onView?.(user)
													}
													aria-label="View user"
												>
													<img src={view} alt="" />
												</button>
												<span
													className={
														styles.tooltipText
													}
												>
													View
												</span>
											</div>

											<div className={styles.tooltip}>
												<button
													className={styles.iconbtn}
													aria-label="Send message"
												>
													<img src={msg} alt="" />
												</button>
												<span
													className={
														styles.tooltipText
													}
												>
													Message
												</span>
											</div>

											<div className={styles.tooltip}>
{/* 												
												<button
	className={`${styles.iconbtn} ${
		!user.canDelete ? styles.disabledIconbtn : ''
	}`}
	onClick={() => {
		if (!user.canDelete) return;

		onDelete?.(user);
	}}
	disabled={!user.canDelete}
	aria-label={
		user.canDelete
			? 'Delete user'
			: 'You cannot remove this user because you do not own any of their programs'
	}
>
	<img src={deletee} alt="" />
</button> */}
<button
    className={styles.iconbtn}
    onClick={() => onEdit?.(user)}
    aria-label="Edit user"
>
    <img src={edit} alt="Edit" />
</button>

<span className={styles.tooltipText}>
    Edit
</span>
												{/* <span
													className={
														styles.tooltipText
													}
												>
													Delete
												</span> */}
											</div>
										</div>
									</div>

									<div className={styles.detailBlock}>
										<div className={styles.label}>
											Program
										</div>
										<div
											className={styles.value}
											title={user.enrolledProgram}
										>
											{formatPrograms(
												user.enrolledProgram
											)}
										</div>
										{/* <div className={styles.value}>{user.enrolledProgram ?? "No program enrolled"}</div> */}
									</div>

									<div className={styles.detailBlock}>
										<div className={styles.label}>
											ICC Credentials
										</div>
										<div className={styles.value}>
											{user.iccCredentials ?? 'N/A'}
										</div>
									</div>
								</div>
							) : (
								/* ✅ DESKTOP TABLE ROW */
								<div key={user.id} className={styles.row}>
									<div className={styles.nameCell}>
										<div className={styles.avatar}>
											{user.avatarUrl ? (
												// <img src={user.avatarUrl} alt={user.fullName} />
												<img
													src={
														user.avatarUrl ||
														profileDefault
													}
													alt={user.fullName}
													onError={e => {
														const target =
															e.target as HTMLImageElement;
														target.onerror = null; // prevent infinite loop
														target.src =
															profileDefault;
													}}
												/>
											) : (
												<div
													className={
														styles.placeholderAvatar
													}
												/>
											)}
										</div>

										<span className={styles.fullName}>
											{user.fullName}
										</span>
									</div>

									{/* <div>{user.enrolledProgram ?? "No program enrolled"}</div> */}
									<div title={user.enrolledProgram}>
										{formatPrograms(user.enrolledProgram)}
									</div>
									<div>{user.iccCredentials ?? 'N/A'}</div>
									<div>{renderRole(user.role)}</div>

									<div className={styles.actions}>
										<div className={styles.tooltip}>
											<button
												className={styles.iconbtn}
												onClick={() => onView?.(user)}
												aria-label="View user"
											>
												<img src={view} alt="" />
											</button>
											<span
												className={styles.tooltipText}
											>
												View
											</span>
										</div>
										<div className={styles.tooltip}>
											<button
												className={styles.iconbtn}
												aria-label="Send message"
											>
												<img src={msg} alt="" />
											</button>
											<span
												className={styles.tooltipText}
											>
												Message
											</span>
										</div>

										<div className={styles.tooltip}>
										
											{/* <button
	className={`${styles.iconbtn} ${
		!user.canDelete ? styles.disabledIconbtn : ''
	}`}
	onClick={() => {
		if (!user.canDelete) return;

		onDelete?.(user);
	}}
	disabled={!user.canDelete}
	aria-label={
		user.canDelete
			? 'Delete user'
			: 'You cannot remove this user because you do not own any of their programs'
	}
>
	<img src={deletee} alt="" />
</button> */}
<button
    className={styles.iconbtn}
    onClick={() => onEdit?.(user)}
    aria-label="Edit user"
>
    <img src={edit} alt="Edit" />
</button>

<span className={styles.tooltipText}>
    Edit
</span>
											{/* <span
												className={styles.tooltipText}
											>
												Delete
											</span> */}
										</div>
									</div>
								</div>
							)
						)
					)}
				</div>
			)}

			{!isMobile && sortedUsers.length > 0 && (
				<div className={styles.bottom}>
					{totalPages > 1 && (
						<div className={styles.pagination}>
							{totalPages > 5 && (
								<button
									disabled={currentPage === 1}
									onClick={() => setCurrentPage(p => p - 1)}
								>
									<img src={left} alt="Previous page" />
								</button>
							)}

							{getPageNumbers().map(page =>
								typeof page === 'string' ? (
									<span
										key={page}
										className={styles.ellipsis}
									>
										...
									</span>
								) : (
									<button
										key={page}
										className={
											currentPage === page
												? styles.activePage
												: ''
										}
										onClick={() => setCurrentPage(page)}
									>
										{page}
									</button>
								)
							)}

							{totalPages > 5 && (
								<button
									disabled={currentPage === totalPages}
									onClick={() => setCurrentPage(p => p + 1)}
								>
									<img src={right} alt="Next page" />
								</button>
							)}
						</div>
					)}

					<div className={styles.showingText}>
						Showing {paginatedUsers.length} of{' '}
						{filteredUsers.length}
					</div>
				</div>
			)}
		</div>
	);
};

export default ManageUsers;
